import { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { Copy, Share2 } from 'lucide-react';
import {
  createInvitesBody,
  disclosureLine,
  inviteResultLines,
  inviteResultTitle,
  inviteResults,
  outcomeErrorKey,
  groupNameSatisfied,
  suggestGroupName,
  type HistoryPolicy,
  type InviteResult,
  type Invitee,
} from '../../../lib/group-chat-actions';
import { groupChatAction, handleLostAccess } from '../../../lib/group-chat-api';
import { invalidateChats, patchChatsConversation } from '../../../lib/chats';
import { deleteConversation } from '../../../lib/conversations';
import { abandonedConversation, newGroupCreatable, NEW_GROUP_MAX_PEOPLE, openNewGroup, runNewGroup } from '../../../lib/new-group';
import { apiFetch } from '../../../lib/api-client';
import { API_BASE } from '../../../lib/api-base';
import { conversationCreateBody } from '../../../lib/conversation-space';
import { queryClient, queryKeys } from '../../../lib/query-client';
import type { GroupSummary } from '../../../lib/group-chat';
import type { PickedRecipient } from '../../../lib/recipient-field';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useGroupChatEnabled } from '../../../lib/group-chat-flag';
import { Modal } from '../../Modal';
import { RecipientField } from '../share/RecipientField';
import { Button, IconButton, Input, RadioGroup, SegmentedToggle, Typography } from '../ui';
import { cachedMembersSummary, groupMembersKey, loadGroupSummary } from '../../../lib/use-group-summary';
import {
  groupSetupKindKey,
  groupSetupPrefill,
  memberNoteKey,
  requestSetupChatReload,
  unreachableMembers,
  type GroupSetupCardData,
  type GroupSetupKind,
  type SetupCardRef,
} from '../../../lib/group-setup-card';

// Loaded on demand: the WhatsApp side reaches the settings queries (and through them common-lib's entity barrel), which
// every importer of these dialogs — the chat header among them — would otherwise pull in, and which a unit test cannot
// load (orion-web CLAUDE.md, "a unit test must not import the app's dependency graph").
const WhatsappSetupBody = dynamic(() => import('./WhatsappSetupDialog').then((m) => m.WhatsappSetupBody), { ssr: false });

const MODAL = 'group_add_people';

/** Today, as the YYYY-MM-DD a date input holds (local). */
function todayDay(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatDay(day: string, locale: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  } catch {
    return day;
  }
}

type Step = { kind: 'compose' } | { kind: 'confirmUnknown'; emails: string[] } | { kind: 'results'; results: InviteResult[] };

/**
 * Add people (design §3.1, contract item 6): recipients (the share dialog's own recipient field), ONE history choice
 * for the batch, the D10 disclosure line, and — only on the FIRST share, while the chat is not yet a group — a group
 * name the creator must type — the participants' first names are only its placeholder (never the chat's private title). Unknown addresses are asked
 * about once; then one result per recipient, with the invite link to copy or share when no notice could go out.
 * iOS `AddPeopleSheet` / Android `AddPeopleSheet` hold the same steps.
 */
export function AddPeopleDialog({ conversationId, summary, onClose }: { conversationId: string; summary: GroupSummary | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const [picked, setPicked] = useState<PickedRecipient[]>([]);
  const [policy, setPolicy] = useState<HistoryPolicy>('ALL');
  const [fromDay, setFromDay] = useState(todayDay());
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>({ kind: 'compose' });
  const firstShare = !summary;
  const invitees: Invitee[] = useMemo(() => picked.map((p) => ({ email: p.identifier, name: p.name })), [picked]);
  // The suggestion is a HINT only (the placeholder): a new group is named by the person creating it.
  const suggested = suggestGroupName(invitees, summary);
  const groupName = name.trim();
  const date = formatDay(fromDay, i18n?.language);
  const bi = (action: string) => logModalActionBi({ page_name: 'chat', modal_name: MODAL, action } as never);

  const send = async (confirmUnknown: boolean) => {
    const body = createInvitesBody({ conversationId, invitees, policy, fromDay, ...(firstShare ? { groupName } : {}), confirmUnknown });
    if (!body) return;
    bi(confirmUnknown ? 'invite_anyway' : 'send_invites');
    setBusy(true);
    setError(null);
    const outcome = await groupChatAction('create-invites', body);
    setBusy(false);
    if (outcome.kind !== 'ok') {
      if (outcome.kind === 'refused' && handleLostAccess(conversationId, outcome.refusal)) {
        onClose();
        return;
      }
      setError(t(outcomeErrorKey(outcome)));
      return;
    }
    if (outcome.data.requiresConfirmation === true && !confirmUnknown) {
      const emails = Array.isArray(outcome.data.unknownEmails) ? (outcome.data.unknownEmails as string[]) : [];
      setStep({ kind: 'confirmUnknown', emails });
      return;
    }
    // The chat is a group now (or has new pending invites): every list and the sheet re-read it.
    if (outcome.data.group) patchChatsConversation(conversationId, {});
    void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(conversationId) });
    void queryClient.invalidateQueries({ queryKey: groupMembersKey(conversationId) });
    setStep({ kind: 'results', results: inviteResults(outcome.data.outcomes) });
  };

  if (step.kind === 'results') {
    return (
      <Modal open onClose={onClose} size="minimum" maxHeightPx={650} scrollBody headerSeparator title={t('chat.group.addPeopleTitle')}
        footer={<Button onClick={() => { bi('done'); onClose(); }}>{t('chat.group.done')}</Button>}>
        <InviteResultsList results={step.results} onBi={bi} />
      </Modal>
    );
  }

  const disclosure = invitees.length > 0 ? disclosureLine(policy, invitees, date, t) : null;
  const sendable = invitees.length > 0 && invitees.length <= 10 && (policy !== 'FROM_DATE' || !!fromDay) && groupNameSatisfied(firstShare, name);
  return (
    <Modal
      open
      onClose={onClose}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      title={t('chat.group.addPeopleTitle')}
      footer={
        step.kind === 'confirmUnknown' ? (
          <>
            <Button variant="outlined" disabled={busy} onClick={() => { bi('cancel_unknown'); setStep({ kind: 'compose' }); }}>{t('chat.group.cancel')}</Button>
            <Button loading={busy} onClick={() => void send(true)}>{t('chat.group.inviteAnyway')}</Button>
          </>
        ) : (
          <>
            <Button variant="outlined" disabled={busy} onClick={() => { bi('cancel'); onClose(); }}>{t('chat.group.cancel')}</Button>
            <Button loading={busy} disabled={!sendable} onClick={() => void send(false)} data-testid="group-send-invites">{t('chat.group.sendInvites')}</Button>
          </>
        )
      }
    >
      <div data-testid="group-add-people" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* One label: the field's own, named "Email addresses" here (the share dialogs keep "Add people"). */}
          <RecipientField
            label={t('chat.group.emailsLabel')}
            alreadyHasAccess={[]}
            picked={picked}
            onPickedChange={(next) => { setPicked(next.slice(0, 10)); setError(null); if (step.kind !== 'compose') setStep({ kind: 'compose' }); }}
            disabled={busy}
            errorMessage={error ?? undefined}
            bi={{ pageName: 'chat', modalName: MODAL }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Typography variant="h4" color="strong" component="h3" id="group-history-title">{t('chat.group.historyTitle')}</Typography>
          <RadioGroup<HistoryPolicy>
            aria-labelledby="group-history-title"
            value={policy}
            onChange={(next) => { bi(`history_${next.toLowerCase()}`); setPolicy(next); }}
            options={[
              { value: 'ALL', label: t('chat.group.historyAll') },
              { value: 'FROM_DATE', label: t('chat.group.historyFromDate') },
              { value: 'AFTER_JOIN', label: t('chat.group.historyAfterJoin') },
            ]}
          />
          {policy === 'FROM_DATE' && (
            <Input type="date" size="medium" value={fromDay} max={todayDay()} onChange={(e) => setFromDay(e.target.value)} aria-label={t('chat.group.historyFromDate')} />
          )}
          {disclosure && (
            <Typography variant="p3" color="secondary" component="p" data-testid="group-disclosure">{disclosure}</Typography>
          )}
        </div>
        {firstShare && (
          <Input
            label={t('chat.group.nameTitle')}
            placeholder={suggested || t('chat.group.namePlaceholder')}
            required
            value={groupName}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            data-testid="group-name-field"
          />
        )}
        {step.kind === 'confirmUnknown' && (
          <Typography variant="p2" color="strong" component="p" role="alert">
            {t('chat.group.unknownConfirm', { emails: step.emails.join(', ') })}
          </Typography>
        )}
      </div>
    </Modal>
  );
}

/** One result per invited person (Added / Invited + why / the link to copy or share) — Add people and New group. */
export function InviteResultsList({ results, onBi }: { results: InviteResult[]; onBi: (action: string) => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (link: string) => {
    onBi('copy_invite_link');
    try {
      await navigator.clipboard.writeText(link);
      setCopied(link);
    } catch {
      setCopied(null);
    }
  };
  const share = async (link: string) => {
    onBi('share_invite_link');
    try {
      if (typeof navigator.share === 'function') await navigator.share({ url: link });
      else await copy(link);
    } catch {
      // the reader closed the share sheet
    }
  };
  return (
    <ul data-testid="group-invite-results" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {results.map((r) => (
        <li key={r.email} data-testid={`group-invite-result-${r.kind}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant="p2" color="strong" component="span">{inviteResultTitle(r)}</Typography>
          {/* R5: Added, or Invited with the reason (not on Symphony yet / their privacy setting). */}
          {inviteResultLines(r).map((key) => (
            <Typography key={key} variant="p3" color="secondary" component="span">{t(key, { name: inviteResultTitle(r) })}</Typography>
          ))}
          {r.kind === 'shareLink' && (
            <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input value={r.link} readOnly size="small" aria-label={t('chat.group.copyLink')} />
                <IconButton size="small" variant="outlined" aria-label={t('chat.group.copyLink')} onClick={() => void copy(r.link)}><Copy size={16} aria-hidden /></IconButton>
                <IconButton size="small" variant="outlined" aria-label={t('chat.group.shareLink')} onClick={() => void share(r.link)}><Share2 size={16} aria-hidden /></IconButton>
              </span>
              {copied === r.link && <Typography variant="p3" color="secondary" component="span" role="status">{t('chat.group.linkCopied')}</Typography>}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

const NEW_GROUP_MODAL = 'group_new';

/** POST /api/conversations — the same fresh, Space-less conversation New chat creates on its first send. */
async function createFreshConversation(): Promise<string | null> {
  const res = await apiFetch(`${API_BASE}/api/conversations`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(conversationCreateBody(undefined)),
  });
  if (!res.ok) return null;
  const data = (await res.json().catch(() => null)) as { conversation?: { id?: string } } | null;
  return data?.conversation?.id || null;
}

/**
 * New group (Chats screen, the Groups empty state, and `new-group` links): a name, the people (the Add-people
 * recipient field and its unknown-address confirm), then Create — a fresh chat, invited as `ALL` (it has no history
 * yet). The rules live in `lib/new-group.ts`; iOS `NewGroupSheet` / Android `NewGroupSheet` hold the same steps.
 */
export function NewGroupDialog({ onClose, onOpenGroup, setup }: {
  onClose: () => void;
  onOpenGroup: (conversationId: string) => void;
  /**
   * Opened from the Maestro's group-setup card ("Review and create"): the dialog starts PREFILLED with the card's name
   * and members, offers the Symphony / WhatsApp switch at the top, and every create carries the card row so the server
   * turns the card into the group's status card (`lib/group-setup-card.ts`). iOS / Android `NewGroupSheet` the same.
   */
  setup?: { card: GroupSetupCardData; ref: SetupCardRef | null };
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  // The Symphony option exists only with Symphony group chat on for this reader; otherwise a setup card opens WhatsApp.
  const symphonyAvailable = useGroupChatEnabled();
  const prefill = useMemo(() => (setup ? groupSetupPrefill(setup.card, symphonyAvailable) : null), [setup, symphonyAvailable]);
  const [kind, setKind] = useState<GroupSetupKind>(prefill?.kind ?? 'SYMPHONY');
  const [picked, setPicked] = useState<PickedRecipient[]>(() => prefill?.symphonyPicked.slice(0, NEW_GROUP_MAX_PEOPLE) ?? []);
  const [name, setName] = useState(prefill?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>({ kind: 'compose' });
  // The chat this dialog created — reused by every retry, deleted if the dialog closes before it became a group.
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const invitees: Invitee[] = useMemo(() => picked.map((p) => ({ email: p.identifier, name: p.name })), [picked]);
  const bi = (action: string) => logModalActionBi({ page_name: 'chats', modal_name: NEW_GROUP_MODAL, action } as never);

  const close = () => {
    const orphan = abandonedConversation(conversationId, created !== null);
    if (orphan) void deleteConversation(orphan);
    onClose();
  };

  const send = async (confirmUnknown: boolean) => {
    if (!newGroupCreatable(name, invitees)) return;
    bi(confirmUnknown ? 'invite_anyway' : 'create_group');
    setBusy(true);
    setError(null);
    const next = await runNewGroup(
      { name, invitees, conversationId, confirmUnknown, setupCard: setup?.ref ?? null },
      { createConversation: createFreshConversation, createInvites: (body) => groupChatAction('create-invites', body), loadSummary: loadGroupSummary },
    );
    setBusy(false);
    if (next.kind === 'createFailed') {
      setError(t('chat.group.createFailed'));
      return;
    }
    setConversationId(next.conversationId);
    if (next.kind === 'inviteFailed') {
      setError(t(outcomeErrorKey(next.outcome)));
      setStep({ kind: 'compose' });
      return;
    }
    if (next.kind === 'confirmUnknown') {
      setStep({ kind: 'confirmUnknown', emails: next.emails });
      return;
    }
    setCreated(next.conversationId);
    // The new group is in the chat list NOW (2026-11-01 hands-on: it appeared only on a later re-read): the chats-v1
    // list re-reads at once, not only the legacy conversations list.
    invalidateChats();
    void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(next.conversationId) });
    // The `members` read was just forced and stored (runNewGroup's `loadSummary`) — re-reading it would only race it.
    // From a setup card: the server wrote `created` onto the card row — the chat re-reads, so it becomes the status card.
    if (setup?.ref) requestSetupChatReload(setup.ref.conversationId);
    setStep({ kind: 'results', results: next.results });
  };

  if (step.kind === 'results' && created) {
    return (
      <Modal open onClose={onClose} size="minimum" maxHeightPx={650} scrollBody headerSeparator title={t('chat.group.newGroupTitle')}
        footer={<Button data-testid="group-open-new" loading={opening} onClick={() => {
          bi('open_group');
          setOpening(true);
          void openNewGroup(created, { known: (id) => cachedMembersSummary(id) !== null, load: loadGroupSummary, open: onOpenGroup }).finally(() => setOpening(false));
        }}>{t('chat.group.openGroup')}</Button>}>
        <InviteResultsList results={step.results} onBi={bi} />
      </Modal>
    );
  }

  // The Symphony / WhatsApp switch, only from a setup card: the name and the card carry over between the two.
  const kindSwitch = setup && symphonyAvailable ? (
    <SegmentedToggle<GroupSetupKind>
      ariaLabel={t('chat.groupSetup.title')}
      segmentWidth="fill"
      value={kind}
      onChange={(next) => { bi(next === 'WHATSAPP' ? 'kind_whatsapp' : 'kind_symphony'); setKind(next); setError(null); }}
      disabled={busy || conversationId !== null}
      options={[
        { value: 'SYMPHONY', label: t(groupSetupKindKey('SYMPHONY')) },
        { value: 'WHATSAPP', label: t(groupSetupKindKey('WHATSAPP')) },
      ]}
    />
  ) : null;

  if (setup && kind === 'WHATSAPP') {
    return (
      <WhatsappSetupBody
        card={setup.card}
        setupRef={setup.ref}
        initialInvitees={prefill?.waInvitees ?? []}
        name={name}
        onNameChange={setName}
        kindSwitch={kindSwitch}
        onClose={close}
      />
    );
  }

  const unreachable = setup ? unreachableMembers(setup.card, 'SYMPHONY') : [];
  const creatable = newGroupCreatable(name, invitees);
  return (
    <Modal
      open
      onClose={close}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      title={t('chat.group.newGroupTitle')}
      footer={
        step.kind === 'confirmUnknown' ? (
          <>
            <Button variant="outlined" disabled={busy} onClick={() => { bi('cancel_unknown'); setStep({ kind: 'compose' }); }}>{t('chat.group.cancel')}</Button>
            <Button loading={busy} onClick={() => void send(true)}>{t('chat.group.inviteAnyway')}</Button>
          </>
        ) : (
          <>
            <Button variant="outlined" disabled={busy} onClick={() => { bi('cancel'); close(); }}>{t('chat.group.cancel')}</Button>
            <Button loading={busy} disabled={!creatable} onClick={() => void send(false)} data-testid="group-create">{t('chat.group.createGroup')}</Button>
          </>
        )
      }
    >
      <div data-testid="group-new" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {kindSwitch}
        <Input
          label={t('chat.group.nameTitle')}
          placeholder={t('chat.group.namePlaceholder')}
          required
          value={name}
          maxLength={100}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          data-testid="group-name-field"
        />
        <RecipientField
          label={t('chat.group.emailsLabel')}
          alreadyHasAccess={[]}
          picked={picked}
          onPickedChange={(next) => { setPicked(next.slice(0, NEW_GROUP_MAX_PEOPLE)); setError(null); if (step.kind !== 'compose') setStep({ kind: 'compose' }); }}
          disabled={busy}
          errorMessage={error ?? undefined}
          bi={{ pageName: 'chats', modalName: NEW_GROUP_MODAL }}
        />
        {/* Card members this kind cannot reach (no email): named, with the reason, never invited. */}
        {unreachable.length > 0 && setup && (
          <div data-testid="group-setup-unreachable" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {unreachable.map((m, i) => (
              <Typography key={`${m.name}-${i}`} variant="p2" color="secondary" component="p">
                {m.name} · {t(memberNoteKey(m, 'SYMPHONY', setup.card.kind) ?? 'chat.groupSetup.member.noEmail')}
              </Typography>
            ))}
          </div>
        )}
        {step.kind === 'confirmUnknown' && (
          <Typography variant="p2" color="strong" component="p" role="alert">
            {t('chat.group.unknownConfirm', { emails: step.emails.join(', ') })}
          </Typography>
        )}
      </div>
    </Modal>
  );
}

/** A named confirm in front of a destructive group action (remove, leave, end) — the delete confirm's shape. */
export function GroupConfirmDialog({ title, body, confirmLabel, busy, error, onConfirm, onClose }: {
  title: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal open onClose={onClose} width={440} maxHeightPx={650} scrollBody title={title}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} disabled={busy}>{t('chat.group.cancel')}</Button>
          <Button color="destructive" loading={busy} onClick={onConfirm} data-testid="group-confirm">{confirmLabel}</Button>
        </>
      }>
      <Typography variant="p2" color="secondary" component="p">{body}</Typography>
      {error && <Typography variant="p2" component="p" role="alert" style={{ color: 'var(--ds-text-destructive)', marginTop: 8 }}>{error}</Typography>}
    </Modal>
  );
}

/** Rename the group (admins). '' clears the name, and the title falls back to the members' first names. */
export function RenameGroupDialog({ initial, busy, error, onSave, onClose }: {
  initial: string;
  busy: boolean;
  error: string | null;
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  return (
    <Modal open onClose={onClose} width={440} maxHeightPx={650} scrollBody title={t('chat.group.rename')}
      footer={
        <>
          <Button variant="outlined" onClick={onClose} disabled={busy}>{t('chat.group.cancel')}</Button>
          <Button loading={busy} onClick={() => onSave(value.trim())} data-testid="group-rename-save">{t('chat.group.save')}</Button>
        </>
      }>
      <Input aria-label={t('chat.group.namePlaceholder')} placeholder={t('chat.group.namePlaceholder')} value={value} maxLength={100} onChange={(e) => setValue(e.target.value)} />
      {error && <Typography variant="p2" component="p" role="alert" style={{ color: 'var(--ds-text-destructive)', marginTop: 8 }}>{error}</Typography>}
    </Modal>
  );
}
